import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import BreadCrumb from "../Controls/BreadCrumb";
import CustomButton from "../Controls/CustomButton";
import API from "../../API";
import { toast } from "react-toastify";
import { refreshSessionShell } from "../../query/useSessionShell";

export default function ChatView() {
  const { id: idStr } = useParams();
  const id = Number.parseInt(idStr, 10);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const markedReadForId = useRef(null);

  async function load() {
    const sessions = await API.getAsync("/Chat/Sessions");
    if (sessions?.ok) {
      const list = sessions.jsonData || [];
      const mine = list.find((s) => (s.ID ?? s.id) === id);
      setSession(mine || null);
    }
    const msgs = await API.getAsync(`/Chat/${id}/Messages`);
    if (msgs?.ok) setMessages(msgs.jsonData || []);
  }

  useEffect(() => {
    if (!id) return;
    markedReadForId.current = null;
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [id]);

  useEffect(() => {
    if (!id || !session) return;
    const status = session.Status ?? session.status;
    const active = status === 2 || status === "Active";
    if (!active || markedReadForId.current === id) return;
    markedReadForId.current = id;
    (async () => {
      const resp = await API.postAsync(`/Chat/${id}/Read`, {});
      if (resp?.ok) {
        setSession(resp.jsonData || { ...session, HasUnread: false, hasUnread: false });
        await refreshSessionShell(queryClient);
      }
    })();
  }, [id, session, queryClient]);

  async function send() {
    const text = (body || "").trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const resp = await API.postAsync(`/Chat/${id}/Message`, { Body: text });
      if (!resp?.ok) {
        toast.error(resp?.jsonData?.message || "Send failed");
        return;
      }
      setBody("");
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function accept() {
    const resp = await API.postAsync(`/Chat/${id}/Accept`, {});
    if (resp?.ok) {
      toast.success("Chat accepted");
      markedReadForId.current = null;
      await load();
    } else toast.error(resp?.jsonData?.message || "Accept failed");
  }

  async function decline() {
    const resp = await API.postAsync(`/Chat/${id}/Decline`, {});
    if (resp?.ok) {
      toast.info("Chat declined");
      navigate("/connections");
    } else toast.error(resp?.jsonData?.message || "Decline failed");
  }

  const status = session?.Status ?? session?.status;
  const isRequester = session?.IsRequester ?? session?.isRequester;
  const hasUnread = !!(session?.HasUnread ?? session?.hasUnread);
  const otherName = `${session?.OtherFirstName ?? session?.otherFirstName ?? ""} ${
    session?.OtherLastName ?? session?.otherLastName ?? ""
  }`.trim();

  return (
    <div className="pagebody" data-testid="chat-view">
      <BreadCrumb props={[{ route: "/connections", label: "Connections" }, "Chat"]} />
      <div className="tabbody">
        <h2>Chat {otherName ? `with ${otherName}` : ""}</h2>
        {hasUnread ? <p className="byline">Unread messages in this chat</p> : null}
        {status === 1 || status === "Requested" ? (
          <div className="tabsection">
            <p>Chat request pending.</p>
            {!isRequester ? (
              <div className="form-actions" style={{ justifyContent: "flex-start" }}>
                <CustomButton type="button" variant="contained" color="primary" onClick={accept}>
                  Accept
                </CustomButton>
                <CustomButton type="button" variant="contained" color="error" onClick={decline}>
                  Decline
                </CustomButton>
              </div>
            ) : (
              <p className="byline">Waiting for the other person to accept.</p>
            )}
          </div>
        ) : null}

        <div className="tabsection" data-testid="chat-messages">
          {(messages || []).map((m) => (
            <div key={m.ID ?? m.id} className="mobilePanel">
              <span className="byline">
                Person {m.FromPersonID ?? m.fromPersonID} ·{" "}
                {m.SentUtc ?? m.sentUtc}
              </span>
              <div>{m.Body ?? m.body}</div>
            </div>
          ))}
          {(messages || []).length === 0 ? <p className="byline">No messages yet.</p> : null}
        </div>

        {status === 2 || status === "Active" ? (
          <div className="field">
            <input
              id="chatBody"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type a message"
              aria-label="Chat message"
            />
            <CustomButton type="button" variant="contained" color="primary" disabled={busy} onClick={send}>
              Send
            </CustomButton>
          </div>
        ) : null}
      </div>
    </div>
  );
}
